import { ReactNode, useMemo, useState } from 'react';

import {
  Card,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  Line,
  ShadowDivider,
  TimeXAxis,
  YAxis,
  Box,
  Stack,
} from '@cast/design-system';
import { compactNumber, dashesOrNum, toNumOrUndefined } from '@cast/utils';

import {
  buildAnalyzePrompt,
  useAnalyzeWithOpsPilot,
} from '@cast/features-organization-shared-opspilot';
import { useReactRouterStrategy } from 'components/chart/hooks';
import { LargeMetricsBox } from 'features/cost-report/components/cost-over-time/LargeMetricsBox';
import { ReportSection } from 'features/cost-report/components/ReportSection';
import {
  QUERY_PARAM_KEYS,
  useCostReportTimeRangeSelection,
} from 'features/cost-report/hooks/useCostReportTimeRangeSelection';
import { RangePickerQueryParamKeys } from '@cast/shared-date-ui';

import { ReliabilitySegmentDot } from './ReliabilitySegmentDot';
import { ReliabilityMetricConfig } from '../_constants/reliabilityMetrics';
import { buildPairedGapEdges } from '../_utils/buildPairedGapEdges';
import {
  ReliabilityMetricsData,
  ReliabilityDatapoint,
} from '../_utils/buildReliabilityChartData';
import {
  buildLatencyTickFormatter,
  formatLatency,
  formatLatencyWithUnit,
  isLatencyMetricKey,
} from '../_utils/formatLatency';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type ReliabilityChartSectionProps = {
  title: string;
  metrics: ReliabilityMetricConfig[];
  chartData: ReliabilityDatapoint[];
  isLoading?: boolean;
  data?: ReliabilityMetricsData;
  timezone: string;
  testId?: string;
  syncId?: string;
  zoomParamKeys?: RangePickerQueryParamKeys;
};

type MetricSummaryDisplay = {
  primaryValue: ReactNode;
  secondaryValue: string | undefined;
};

const getMetricSummaryDisplay = (
  metric: ReliabilityMetricConfig,
  summary: number | undefined
): MetricSummaryDisplay => {
  if (isLatencyMetricKey(metric.key)) {
    const formatted = formatLatency(summary, 2);
    return {
      primaryValue: formatted?.value ?? '--.--',
      secondaryValue:
        summary !== undefined ? formatted?.unit ?? 'ms' : undefined,
    };
  }

  return {
    primaryValue: dashesOrNum({ value: summary, roundBy: 2 }),
    secondaryValue: summary !== undefined ? metric.unit : undefined,
  };
};

const getIsLatencyChart = (metrics: ReliabilityMetricConfig[]): boolean => {
  return metrics.every(({ key }) => isLatencyMetricKey(key));
};

const getTooltipUnit = (
  metric: ReliabilityMetricConfig,
  isLatencyChart: boolean
): string | undefined => {
  return isLatencyChart ? undefined : metric.unit;
};

const formatChartTooltipValue = (
  value: string | number | undefined,
  isLatencyChart: boolean
) => {
  if (isLatencyChart) {
    return formatLatencyWithUnit(value, 2);
  }

  return dashesOrNum({ value, roundBy: 2 });
};

const trafficTickFormatter = (value: number | string): string => {
  const numericValue = toNumOrUndefined(value);
  return numericValue === undefined
    ? String(value)
    : compactNumber(numericValue, { maximumFractionDigits: 2 });
};

const useWorkloadReliabilityZoomState = useReactRouterStrategy.bind(null, {
  queryParamsKeys: QUERY_PARAM_KEYS,
});

const buildZoomState = (keys: RangePickerQueryParamKeys) =>
  useReactRouterStrategy.bind(null, { queryParamsKeys: keys });

const MetricSummaryBox = ({
  metric,
  data,
  testId,
  markerColor,
  onClick,
  disabled,
}: {
  metric: ReliabilityMetricConfig;
  data?: ReliabilityMetricsData;
  testId?: string;
  markerColor?: string;
  onClick?: () => void;
  disabled?: boolean;
}) => {
  const summary = data?.[metric.key]?.summary;
  const { primaryValue, secondaryValue } = getMetricSummaryDisplay(
    metric,
    summary
  );

  return (
    <LargeMetricsBox
      markerColor={markerColor}
      title={metric.label}
      primaryValue={primaryValue}
      secondaryValue={secondaryValue}
      testId={`${testId}-${metric.key}-summary`}
      onClick={onClick}
      disabled={disabled}
    />
  );
};

export const ReliabilityChartSection = ({
  title,
  metrics,
  chartData,
  isLoading = false,
  data,
  timezone,
  testId,
  syncId,
  zoomParamKeys,
}: ReliabilityChartSectionProps) => {
  const { cluster } = useCluster();
  const { getRange } = useCostReportTimeRangeSelection();
  const useZoomState = zoomParamKeys
    ? buildZoomState(zoomParamKeys)
    : useWorkloadReliabilityZoomState;
  const [visibleMetrics, setVisibleMetrics] = useState<Record<string, boolean>>(
    () => Object.fromEntries(metrics.map((m) => [m.key, true]))
  );

  const analyzeWithOpsPilot = useAnalyzeWithOpsPilot({
    buildPrompt: buildAnalyzePrompt({
      context: `${title} metrics`,
      scopeName: cluster.name,
      getDefaultRange: getRange,
    }),
  });
  const toggleMetric = (key: string) => {
    setVisibleMetrics((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const [primaryMetric, ...secondaryMetrics] = metrics;
  const isLatencyChart = getIsLatencyChart(metrics);
  const axisUnit = primaryMetric?.unit;
  const visibleMetricsList = metrics.filter((m) => visibleMetrics[m.key]);
  const metricKeys = useMemo(() => metrics.map((m) => m.key), [metrics]);
  const visibleOrAllMetricKeys = useMemo(() => {
    const visibleMetricKeys = visibleMetricsList.map((m) => m.key);
    return visibleMetricKeys.length ? visibleMetricKeys : metricKeys;
  }, [visibleMetricsList, metricKeys]);
  const latencyTickFormatter = useMemo(
    () =>
      isLatencyChart
        ? buildLatencyTickFormatter(chartData, visibleOrAllMetricKeys)
        : undefined,
    [chartData, isLatencyChart, visibleOrAllMetricKeys]
  );
  const pairedGapEdges = useMemo(
    () =>
      Object.fromEntries(
        metrics.map((m) => [m.key, buildPairedGapEdges(chartData, m.key)])
      ),
    [chartData, metrics]
  );

  return (
    <ReportSection title={title} testId={testId}>
      <Card sx={{ p: 0, mt: 8 }}>
        {primaryMetric && (
          <Box
            display="flex"
            justifyContent="space-between"
            py={12}
            px={16}
            gap={16}
          >
            <Box flex={1}>
              <MetricSummaryBox
                metric={primaryMetric}
                data={data}
                testId={testId}
                onClick={() => toggleMetric(primaryMetric.key)}
                disabled={!visibleMetrics[primaryMetric.key]}
              />
            </Box>
            {!!secondaryMetrics.length && (
              <Stack flex={1} direction="row" justifyContent="space-between">
                {secondaryMetrics.map((metric) => (
                  <Box key={`summary-${metric.key}`} width="100%">
                    <MetricSummaryBox
                      metric={metric}
                      data={data}
                      testId={testId}
                      markerColor={metric.stroke}
                      onClick={() => toggleMetric(metric.key)}
                      disabled={!visibleMetrics[metric.key]}
                    />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        )}
        <ShadowDivider opacity={0.2} size={16} />
        <Box height={240} px={16} pt={8} pb={8}>
          <ChartContainer
            data={chartData ?? []}
            isLoading={isLoading}
            hasNoData={!isLoading && !chartData.length}
            margin={{ top: 12, left: 0, right: 0, bottom: 0 }}
            syncId={syncId}
            zoomProps={{
              xAxisDataKey: 'chartTimestampUnix',
              useZoomState,
            }}
            analyzeWithOpsPilot={analyzeWithOpsPilot}
          >
            <ChartGrid hideVertical />
            {visibleMetricsList.map((metric) => (
              <Line<ReliabilityDatapoint>
                key={metric.key}
                dataKey={metric.key}
                stroke={metric.stroke}
                connectNulls={false}
                isAnimationActive={false}
                tooltipConfig={{
                  displayName: metric.label,
                  unit: getTooltipUnit(metric, isLatencyChart),
                }}
                dot={
                  <ReliabilitySegmentDot
                    activeDotIndices={pairedGapEdges[metric.key]}
                  />
                }
              />
            ))}
            <TimeXAxis<ReliabilityDatapoint>
              unixDataKey="chartTimestampUnix"
              timezone={timezone}
            />
            <YAxis<ReliabilityDatapoint>
              domain={[
                'dataMin',
                (dataMax: number) => Math.max(dataMax * 1.2, 1),
              ]}
              allowDecimals
              tickFormatter={latencyTickFormatter ?? trafficTickFormatter}
              unitFormatter={
                !isLatencyChart && axisUnit
                  ? (formattedValue) => `${formattedValue} ${axisUnit}`
                  : undefined
              }
            />
            <ChartTooltip<ReliabilityDatapoint>
              header={(headerProps) => (
                <ChartTooltipTimestampHeader
                  {...headerProps}
                  timezone={timezone}
                />
              )}
              formatValue={(value) =>
                formatChartTooltipValue(value, isLatencyChart)
              }
            />
          </ChartContainer>
        </Box>
      </Card>
    </ReportSection>
  );
};
